import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import classNames from "classnames";
import styles from "./ModernNavbar.module.scss";
import OnlineIcon from "../../assets/icons/AccountWeb.svg?react";
import useAppModernisation from "../../hooks/useAppModernisation";
import MobileHeader from "../Layout/MobileHeader";
import { getNavigationItems } from "../../constants/navigation";
import GuidelinesDropdown from "./GuidelinesDropdown";
import VersionIndicatorBanner from "../ModernImport/VersionIndicatorBanner";
import AppsMenu from "../AppsMenu/AppsMenu";
import useAuth from "../../hooks/useAuth";
import useNetworkStatus from "../../hooks/useNetworkStatus";

const ModernNavbar = () => {
  const { isAppMode, isWebModernisationEnabled } = useAppModernisation();
  const {
    subscriptionDetails,
    isLoading = false,
    subscriptionError = null,
    subscriptionCheckAttempted = false,
  } = useAuth();

  // Use same Redux selectors as AccountProfile for consistent progress display
  const [isActualDesktop, setIsActualDesktop] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [hideTimeout, setHideTimeout] = useState(null);
  const navbarRef = React.useRef(null);
  const navItemRefs = React.useRef({});
  const { isOnline } = useNetworkStatus();

  const shouldShowApps =
    !isLoading &&
    !subscriptionError &&
    !!subscriptionDetails &&
    subscriptionCheckAttempted &&
    !subscriptionDetails.isMultiUser &&
    !isAppMode &&
    isOnline;

  useEffect(() => {
    const handleResize = () => {
      setIsActualDesktop(window.innerWidth >= 991);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Set navbar height as CSS variable for submenus
  useEffect(() => {
    if (navbarRef.current) {
      const navbarHeight = navbarRef.current.offsetHeight;
      document.documentElement.style.setProperty("--navbar-height", `${navbarHeight}px`);
    }
  });

  // Update submenu positions when hovering
  useEffect(() => {
    if (hoveredItem && navItemRefs.current[hoveredItem]) {
      const navItem = navItemRefs.current[hoveredItem];
      const rect = navItem.getBoundingClientRect();

      if (hoveredItem !== "guidelines") {
        // For Calculators and Resources, align left edge of submenu with actual text content
        // Account for the nav link's horizontal padding: clamp(0.75rem, 2vw, 1.25rem)
        const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
        const viewportWidth = window.innerWidth;

        // Calculate the clamp value: clamp(0.75rem, 2vw, 1.25rem)
        const minPadding = 0.75 * rootFontSize; // 0.75rem in px
        const preferredPadding = (2 * viewportWidth) / 100; // 2vw in px
        const maxPadding = 1.25 * rootFontSize; // 1.25rem in px

        const horizontalPadding = Math.max(minPadding, Math.min(preferredPadding, maxPadding));

        const leftPosition = rect.left - horizontalPadding;
        document.documentElement.style.setProperty(
          `--submenu-${hoveredItem}-left`,
          `${leftPosition}px`,
        );
      }
    }
  }, [hoveredItem]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hideTimeout) {
        clearTimeout(hideTimeout);
      }
    };
  }, [hideTimeout]);

  useEffect(() => {
    const handleScroll = () => {
      if (hideTimeout) {
        clearTimeout(hideTimeout);
        setHideTimeout(null);
      }
      setHoveredItem(null);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [hideTimeout]);

  const handleMouseEnter = (itemId) => {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      setHideTimeout(null);
    }
    setHoveredItem(itemId);
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setHoveredItem(null);
    }, 150); // 150ms delay before hiding
    setHideTimeout(timeout);
  };

  const navigationItems = getNavigationItems();
  console.log("ModernNavbar - navigationItems:", navigationItems);

  return (
    <nav
      ref={navbarRef}
      className={classNames(styles.modernNavbar, {
        [styles.modernised]: isWebModernisationEnabled,
      })}
    >
      <div className={styles.navContainer}>
        {/* Use existing MobileHeader for mobile/tablet */}
        {!isActualDesktop && (
          <div className={styles.mobileHeaderWrapper}>
            <MobileHeader />
          </div>
        )}

        {/* Desktop Navigation */}
        {isActualDesktop && (
          <>
            <Link to="/" className={styles.logoLink}>
              <img
                src="/TherapeuticGuidelines_Original_R_Logo.svg"
                alt="Therapeutic Guidelines"
                className={styles.logo}
              />
            </Link>

            <div className={styles.desktopNav}>
              <ul className={styles.navList}>
                {navigationItems.map((item) => (
                  <li
                    key={item.id}
                    className={styles.navItem}
                    ref={(el) => (navItemRefs.current[item.id] = el)}
                    onMouseEnter={() => handleMouseEnter(item.id)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <div className={styles.navLinkWrapper}>
                      <Link
                        to={item.path}
                        className={classNames(styles.navLink, {
                          [styles.activeSubmenu]:
                            hoveredItem === item.id &&
                            item.subMenuItems &&
                            item.subMenuItems.length > 0,
                        })}
                      >
                        <span className={styles.navText}>{item.title}</span>
                      </Link>
                      {/* Submenu rendering - special case for guidelines */}
                      {item.subMenuItems &&
                        item.subMenuItems.length > 0 &&
                        hoveredItem === item.id && (
                          <>
                            {item.id === "guidelines" ? (
                              <GuidelinesDropdown />
                            ) : (
                              <div
                                className={classNames(styles.submenu, styles[`submenu-${item.id}`])}
                              >
                                <ul>
                                  {item.subMenuItems.map((sub, idx) => (
                                    <li key={sub.id || idx}>
                                      {sub.isExternal ? (
                                        <a
                                          href={sub.externalUrl}
                                          className={styles.submenuLink}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          <span>{sub.title}</span>
                                        </a>
                                      ) : (
                                        <Link to={sub.path} className={styles.submenuLink}>
                                          <span>{sub.title}</span>
                                        </Link>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </>
                        )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className={styles.accountSection}>
              {shouldShowApps && <AppsMenu iconSize={32} />}
              <Link
                data-testid="account-person"
                to="/app/accountLanding"
                className={styles.accountLink}
              >
                <OnlineIcon className={styles.accountIcon} />
              </Link>
            </div>
            <VersionIndicatorBanner />
          </>
        )}
      </div>
    </nav>
  );
};

export default ModernNavbar;
